import { inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Factory } from '../factory';
import { IAccountInfoForm } from '../../entities/account-info-entity';
import { isDate } from '../../validators/isDate';
import { UserDto, UserDtoWithProfilePictureBuffer } from '@web/dtos';

@Injectable({
  providedIn: 'root',
})
export class AccountInfoFactoryService extends Factory<
  IAccountInfoForm,
  UserDto
> {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  buildForm(
    entity: UserDtoWithProfilePictureBuffer | UserDto | undefined
  ): FormGroup<IAccountInfoForm> {
    return this.formBuilder.group({
      userId: this.formBuilder.nonNullable.control(entity?.userId ?? '', []),
      profilePictureId: this.formBuilder.control(
        entity?.profilePictureId ?? '',
        []
      ),
      salutation: this.formBuilder.nonNullable.control(
        entity?.salutation ?? '',
        [Validators.required]
      ),
      firstName: this.formBuilder.nonNullable.control(entity?.firstName ?? '', [
        Validators.required,
      ]),
      lastName: this.formBuilder.nonNullable.control(entity?.lastName ?? '', [
        Validators.required,
      ]),
      dateOfBirth: this.formBuilder.nonNullable.control(
        entity?.birthDate ?? '',
        [Validators.required, isDate()]
      ),
      email: this.formBuilder.nonNullable.control(entity?.email ?? '', [
        Validators.required,
        Validators.email,
      ]),
      phoneNumber: this.formBuilder.nonNullable.control(
        entity?.phoneNumber ?? ''
      ),
      dateOfTermsAgreement: this.formBuilder.nonNullable.control(
        entity?.dateOfTermsAgreement ?? null,
        []
      ),
      id: this.formBuilder.nonNullable.control(entity?.address?.id ?? ''),
      country: this.formBuilder.nonNullable.control(
        entity?.address?.country ?? '',
        [Validators.required]
      ),
      cityCode: this.formBuilder.nonNullable.control(
        entity?.address?.postCode ?? '',
        [Validators.required]
      ),
      city: this.formBuilder.nonNullable.control(entity?.address?.city ?? '', [
        Validators.required,
      ]),
      street: this.formBuilder.nonNullable.control(
        entity?.address?.street ?? '',
        [Validators.required]
      ),
      streetNumber: this.formBuilder.nonNullable.control(
        entity?.address?.houseNumber ?? '',
        [Validators.required]
      ),
      streetNumberSuffix: this.formBuilder.nonNullable.control(
        entity?.address?.additional ?? '',
        []
      ),
      referenceToCustomer: this.formBuilder.nonNullable.control(
        entity?.referenceToCustomer ?? '',
        []
      ),
      profilePicture: this.formBuilder.control<Buffer<ArrayBuffer> | null>(
        (entity as UserDtoWithProfilePictureBuffer | undefined)
          ?.profilePicture ?? null
      ),
    });
  }

  override buildEntity(form: FormGroup<IAccountInfoForm>): UserDto {
    const accountInfo = form.getRawValue();
    return {
      address: {
        city: accountInfo.city,
        country: accountInfo.country,
        firstName: accountInfo.firstName,
        houseNumber: accountInfo.streetNumber,
        lastName: accountInfo.lastName,
        postCode: accountInfo.cityCode,
        street: accountInfo.street,
        additional: accountInfo.streetNumberSuffix,
      },
      dateOfTermsAgreement: accountInfo.dateOfTermsAgreement,
      birthDate: accountInfo.dateOfBirth,
      email: accountInfo.email,
      firstName: accountInfo.firstName,
      lastName: accountInfo.lastName,
      phoneNumber: accountInfo.phoneNumber,
      salutation: accountInfo.salutation,
      userId: accountInfo.userId,
      referenceToCustomer: accountInfo.referenceToCustomer,
    };
  }
}
